<template>
  <div class="h-full p-6 overflow-y-auto w-full">
    <form
      class="space-y-6"
      @submit.prevent="submit"
    >
      <div class="space-y-4">
        <h3 class="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
          Procurement Method
        </h3>

        <div class="grid grid-cols-2 gap-3">
          <button
            type="button"
            class="btn text-sm py-3"
            :class="[procurementForm.form.procurement_method === 'schedule_of_rates' ? 'btn--primary-blue' : 'btn--secondary-blue']"
            :disabled="hasPackageItems"
            @click="procurementForm.form.procurement_method = 'schedule_of_rates'"
          >
            Schedule of Rates
          </button>

          <button
            type="button"
            class="btn text-sm py-3"
            :class="[procurementForm.form.procurement_method === 'lump_sum' ? 'btn--primary-blue' : 'btn--secondary-blue']"
            :disabled="hasPackageItems"
            @click="procurementForm.form.procurement_method = 'lump_sum'"
          >
            Lump Sum
          </button>
        </div>

        <p
          v-if="hasPackageItems"
          class="text-sm bg-yellow-100 text-yellow-800 p-4 rounded-xl"
        >
          You cannot change the procurement method until you remove all package items.
        </p>
      </div>

      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t('Subcontract.PackagesDashboard.PackageBreakdown.label.formOfSubcontract') }}
          </label>

          <AppInput
            v-model="procurementForm.form.form_of_subcontract"
            name="form_of_subcontract"
            type="text"
            :error="procurementForm.getErrors('form_of_subcontract')"
            class="form-input"
            placeholder="Enter form of subcontract..."
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t('Subcontract.PackagesDashboard.PackageBreakdown.label.riskLevel') }}
          </label>

          <AppSelect
            v-model="procurementForm.form.risk_level"
            name="risk_level"
            :error="procurementForm.getErrors('risk_level')"
            class="form-input capitalize"
            :options="['low', 'medium', 'high']"
            optional
          />
        </div>
      </div>

      <!-- Save Button -->
      <div class="pt-4 border-t border-gray-200">
        <button
          v-if="canSave"
          type="submit"
          class="btn btn--primary-blue flex items-center space-x-2 mt-2"
          :disabled="updateAction.is(States.LOADING)"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>

          <span>Save Procurement Strategy</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import Package from '@/models/Package';
import { usePackagesStore } from '@/store/packages';
import { usePackageTradeItemsStore } from '@/store/packageTradeItems';
import { computed, watch } from 'vue';
import { toast } from 'vue3-toastify';

const props = defineProps<{
  selectedPackage: Package;
  canSave: boolean;
}>();

const packagesStore = usePackagesStore();
const packageTradeItemsStore = usePackageTradeItemsStore();
const updateAction = useStoreApiAction(packagesStore.update);

const procurementForm = useFormData({
  procurement_method: '',
  form_of_subcontract: '',
  risk_level: undefined as string | undefined,
});

watch(() => props.selectedPackage, (pkg) => {
  if(pkg) {
    procurementForm.form.procurement_method = pkg.procurement_method ?? '';
    procurementForm.form.form_of_subcontract = pkg.form_of_subcontract ?? '';
    procurementForm.form.risk_level = pkg.risk_level ?? undefined;
  }
}, { immediate: true });

const hasPackageItems = computed(() => {
  return packageTradeItemsStore.models
    .where('package_id', props.selectedPackage.id)
    .get()
    .length > 0;
});

function submit() {
  procurementForm.resetErrors();

  updateAction
    .request(props.selectedPackage.id, procurementForm.form)
    .then(() => {
      toast.success(t('Subcontract.PackagesDashboard.PackageBreakdown.message.packageDetailsUpdated'));
    })
    .catch((err) => {
      procurementForm.setErrorsFromAxiosError(err);
    });
}
</script>
